import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import classNames from 'classnames';

import { sanitizeDecimalInput } from '../../utils/clampDecimals';

import styles from './Input.module.css';

interface InputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  type?:
    | 'text'
    | 'email'
    | 'number'
    | 'password'
    | 'tel'
    | 'url'
    | 'search'
    | 'date'
    | 'datetime-local'
    | 'month'
    | 'color';
  error?: string;
  /**
   * Cap typed values at this many decimal places (e.g. `2` for money fields).
   * Opt-in, so rate/percentage inputs that need finer precision are unaffected.
   *
   * When set, the field renders as `type="text"` with `inputMode="decimal"`
   * rather than `type="number"`: a text input never localises the decimal
   * separator, so the value stays a canonical `.`-separated string (a native
   * number input re-renders a `.` as `,` under comma locales). A typed comma is
   * normalised to a dot, and the stepper arrows are gone for free.
   */
  maxDecimals?: number;
  /**
   * Show the integer part with thousands separators (e.g. `1,234.50`) while
   * still emitting a canonical comma-free value to `onChange`. Decimal mode only
   * (set `maxDecimals`), since it relies on the text-input display string.
   */
  groupThousands?: boolean;
}

/** Add comma thousands separators to a canonical (comma-free) decimal string. */
function groupInteger(canonical: string): string {
  if (canonical === '' || canonical === '-') return canonical;
  const neg = canonical.startsWith('-');
  const body = neg ? canonical.slice(1) : canonical;
  const dot = body.indexOf('.');
  const intPart = dot === -1 ? body : body.slice(0, dot);
  const rest = dot === -1 ? '' : body.slice(dot); // keeps the dot + any decimals
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + grouped + rest;
}

const stripGroup = (s: string): string => s.replace(/,/g, '');

export function Input({
  className,
  type = 'text',
  error,
  maxDecimals,
  groupThousands = false,
  inputMode,
  value,
  onChange,
  ...props
}: InputProps): JSX.Element {
  const decimalClamped = maxDecimals != null;
  const grouped = decimalClamped && groupThousands;

  // In decimal mode keep a local display string so an in-progress value such as
  // "79." survives a parent that stores the value as a parsed number (a text
  // input reflects its controlled value verbatim, unlike a native number input).
  const [display, setDisplay] = useState(() => {
    const init = value == null ? '' : String(value);
    return grouped ? groupInteger(init) : init;
  });
  useEffect(() => {
    if (!decimalClamped) return;
    const incoming = value == null || value === '' ? '' : String(value);
    setDisplay((cur) => {
      const curCanonical = grouped ? stripGroup(cur) : cur;
      if (incoming === curCanonical) return cur;
      // Keep what the user is typing when it already means the same number,
      // e.g. "79." or "79.50" against an incoming 79 / 79.5.
      const a = parseFloat(incoming);
      if (!Number.isNaN(a) && a === parseFloat(curCanonical)) return cur;
      return grouped ? groupInteger(incoming) : incoming;
    });
  }, [value, decimalClamped, grouped]);

  const handleChange = decimalClamped
    ? (e: React.ChangeEvent<HTMLInputElement>) => {
        // Strip grouping commas first — sanitize treats a comma as a decimal
        // separator, which would corrupt a grouped value.
        const sanitized = sanitizeDecimalInput(
          grouped ? stripGroup(e.target.value) : e.target.value,
          maxDecimals,
        );
        setDisplay(grouped ? groupInteger(sanitized) : sanitized);
        // Emit the canonical comma-free value so parents can parseFloat it.
        if (sanitized !== e.target.value) e.target.value = sanitized;
        onChange?.(e);
      }
    : onChange;

  return (
    <div className={styles.InputWrapper}>
      <input
        type={decimalClamped ? 'text' : type}
        inputMode={decimalClamped ? 'decimal' : inputMode}
        className={classNames(styles.Input, className)}
        aria-invalid={!!error}
        value={decimalClamped ? display : value}
        onChange={handleChange}
        {...props}
      />
      {error && <span className={styles.ErrorMessage}>{error}</span>}
    </div>
  );
}
